import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Phone, Sparkles, Check } from 'lucide-react';
import { FloatingParticles } from '../FloatingParticles';

export const Hero = ({ onBook }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 200]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  const scale = useTransform(scrollY, [0, 600], [1, 1.1]);

  return (
    <section
      id="top"
      className="relative min-h-screen w-full overflow-hidden bg-[#0D1B2A]"
      data-testid="hero-section"
    >
      {/* Layered backgrounds */}
      <motion.div style={{ scale }} className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=2000&q=85&auto=format&fit=crop)',
          }}
        />
        {/* Mild overall tone */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#07111c]/55 via-[#07111c]/35 to-[#1F2A4A]/45" />
        {/* Bottom & top vignette */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#07111c]/80 via-transparent to-[#07111c]/15" />
        {/* Left-side text scrim — keeps headline & copy readable on any background */}
        <div className="absolute inset-y-0 left-0 w-full md:w-2/3 bg-gradient-to-r from-[#07111c]/92 via-[#07111c]/65 to-transparent" />
      </motion.div>

      {/* Gold grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(#C9A84C 1px, transparent 1px), linear-gradient(90deg, #C9A84C 1px, transparent 1px)',
          backgroundSize: '88px 88px',
        }}
        aria-hidden="true"
      />

      {/* Pulsing gold orbs */}
      <div
        className="orb-pulse absolute pointer-events-none rounded-full"
        style={{
          width: '680px',
          height: '680px',
          top: '50%',
          right: '-8%',
          transform: 'translateY(-50%)',
          background: 'radial-gradient(circle, rgba(201,168,76,0.4) 0%, rgba(201,168,76,0.08) 40%, transparent 70%)',
          filter: 'blur(40px)',
        }}
        aria-hidden="true"
      />
      <div
        className="orb-pulse-slow absolute pointer-events-none rounded-full"
        style={{
          width: '320px',
          height: '320px',
          bottom: '8%',
          left: '3%',
          background: 'radial-gradient(circle, rgba(201,168,76,0.35) 0%, transparent 65%)',
          filter: 'blur(50px)',
        }}
        aria-hidden="true"
      />

      {/* Floating gold particles */}
      <FloatingParticles count={20} />

      {/* Grain noise overlay */}
      <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none grain-texture" />

      {/* Soft gold glow */}
      <div className="absolute top-1/3 left-1/4 w-[600px] h-[600px] rounded-full bg-[#C9A84C]/18 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-[#D9BC6E]/12 blur-[120px] pointer-events-none" />
      <div className="absolute top-1/2 right-1/3 w-[400px] h-[400px] rounded-full bg-[#F9F7F4]/8 blur-[100px] pointer-events-none" />

      {/* Content */}
      <motion.div
        style={{ y, opacity }}
        className="relative z-10 min-h-screen flex flex-col justify-center max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 pb-20"
      >
        {/* Brand mark with soft spotlight glow */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="relative mb-10 md:mb-14 inline-block"
        >
          {/* Soft cream halo (brighter) */}
          <div
            className="absolute inset-0 -m-10 md:-m-16 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse at center, rgba(245,240,230,0.65) 0%, rgba(245,240,230,0.35) 35%, rgba(245,240,230,0) 70%)',
              filter: 'blur(14px)',
            }}
            aria-hidden="true"
          />
          {/* Gold accent glow */}
          <div
            className="absolute inset-0 -m-6 pointer-events-none"
            style={{
              background:
                'radial-gradient(circle at 50% 30%, rgba(212,175,55,0.35) 0%, rgba(212,175,55,0) 60%)',
              filter: 'blur(8px)',
            }}
            aria-hidden="true"
          />
          <img
            src="/assets/tsalach-logo.png"
            alt="Tsalach Cleaning Services"
            className="relative h-24 md:h-32 lg:h-36 w-auto object-contain"
            data-testid="hero-logo"
            style={{
              filter:
                'brightness(1.25) contrast(1.1) drop-shadow(0 4px 12px rgba(212,175,55,0.4)) drop-shadow(0 0 28px rgba(245,240,230,0.7))',
            }}
          />
        </motion.div>

        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="flex items-center gap-3 mb-8"
        >
          <div className="h-px w-12 bg-[#C9A84C]" />
          <span className="text-[#C9A84C] text-xs tracking-[0.4em] uppercase font-medium">
            DC · MD · VA
          </span>
        </motion.div>

        {/* Main headline */}
        <div className="max-w-5xl">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-white leading-[0.95] tracking-tight"
            style={{ fontSize: 'clamp(2.5rem, 9vw, 8.5rem)' }}
          >
            <span className="block">Cleaning, refined</span>
            <span> </span>
            <span className="block italic text-[#C9A84C] font-light">
              to an art form.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-10 text-base md:text-lg text-white/85 max-w-xl font-light leading-relaxed"
            style={{ textShadow: '0 2px 12px rgba(20,26,46,0.6)' }}
          >
            White-glove residential, commercial, and turnover cleaning for
            homeowners and businesses who refuse to settle. Discreet teams,
            obsessive detail, and a finish that speaks for itself.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 md:mt-12 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={onBook}
              className="group relative overflow-hidden bg-[#C9A84C] text-[#0D1B2A] px-8 md:px-10 py-4 md:py-5 text-xs tracking-[0.3em] uppercase font-semibold flex items-center justify-center gap-3 transition-all hover:gap-5"
              data-testid="hero-book-btn"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-[#D9BC6E] to-[#C9A84C] translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500" />
              <span className="relative">Get a Free Quote</span>
              <ArrowRight className="relative w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href="tel:5712656239"
              className="group border border-white/30 text-white hover:border-[#C9A84C] hover:text-[#C9A84C] px-8 md:px-10 py-4 md:py-5 text-xs tracking-[0.3em] uppercase font-medium flex items-center justify-center gap-3 transition-colors"
              data-testid="hero-call-btn"
            >
              <Phone className="w-4 h-4" />
              (571) 265-6239
            </a>
          </motion.div>

          {/* Trust micro-badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-white/80"
            data-testid="hero-trust-badges"
          >
            {[
              'Licensed & Insured',
              'Satisfaction Guaranteed',
              'Serving DC · MD · VA',
            ].map((t) => (
              <div key={t} className="flex items-center gap-2 text-xs tracking-wider">
                <Check className="w-3.5 h-3.5 text-[#C9A84C] flex-shrink-0" strokeWidth={2.5} />
                <span>{t}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Bottom row meta */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-16 lg:mt-0 lg:absolute lg:bottom-12 lg:left-12 lg:right-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div className="text-xs text-white/70">
            <div className="text-white">Trusted by 50+ local clients</div>
            <div className="text-white/65 tracking-wider mt-1">Five-star service across DC, MD &amp; VA</div>
          </div>

          <div className="flex items-center gap-2 text-white/60 text-xs tracking-[0.3em] uppercase">
            <Sparkles className="w-3 h-3 text-[#C9A84C]" />
            Scroll to explore
            <div className="ml-2 w-12 h-px bg-white/30 relative overflow-hidden">
              <div className="absolute inset-0 bg-[#C9A84C] animate-scroll-line" />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};
